import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Button,
  Tooltip,
  Toggle,
  
} from "@carbon/react";
import { Information } from "@carbon/icons-react";
import CommonFields from "../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  onSaveSuccess,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // ---------- STATE ----------
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Form state
  const [form, setForm] = useState({
    plotArea: "",
    length: "",
    width: "",
    floorsAllowed: "",
    boundaryWall: null,
    gatedProject: false,
    plotAreaUnit: "",
    facing: "",
    projectName: "", 
  });

  // ---------- UNIT CONVERSION FACTORS (base = sq.ft) ----------
const UNIT_TO_SQFT = useMemo(
  () => ({
    "sqft": 1,
    "sq.ft": 1,
    "sqm": 10.7639,
    "sq.m": 10.7639,
    "sqyd": 9,
    "sq.yd": 9,
    "cent": 435.6,
    "acre": 43560,
    "hectare": 107639,
    "ground": 2400,
    "guntha": 1089,
  }),
  []
);

  // ---------- MAPPING ----------
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      plotArea: "land_plot_area",
      length: "length",
      width: "width",
      floorsAllowed: "floors_allowed_for_construction",
      plotAreaUnit: "plot_area_unit",
      boundaryWall: "boundary_wall",
      gatedProject: "gated_project",
      facing: "facing",
      projectName: "project_name", 
    }),
    []
  );

  // ---------- VALIDATION RULES ----------
  const validationRules = useMemo(
    () => ({
plotArea: {
  required: true,
  custom: (value) => {
    if (!value || value === "") return "Plot Area is required";

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return "Plot Area must be a valid number";
    }

    if (numValue <= 0) {
      return "Plot Area must be greater than 0";
    }

    return null;
  },
},
      plotAreaUnit: { 
        required: true, 
        message: "Plot Area unit is required" 
      },
      length: {
        custom: (value) => {
          if (!value || value === "") return null;
          const numValue = Number(value);
          if (isNaN(numValue)) return "Length must be a valid number";
          if (numValue <= 0) return "Length must be greater than 0";
          return null;
        },
      },
width: {
  custom: (value) => {
    if (!value || value === "") return null;

    const numValue = Number(value);

    if (isNaN(numValue)) {
      return "Width must be a valid number";
    }

    if (numValue <= 0) {
      return "Width must be greater than 0";
    }

    return null;
  },
},
      // floorsAllowed: {
      //   required: true,
      //   custom: (value) => {
      //     if (!value || value === "") return "Floors allowed is required";
      //     const numValue = Number(value);
      //     if (isNaN(numValue)) return "Floors allowed must be a valid number";
      //     if (numValue <= 0) return "Floors allowed must be greater than 0";
      //     if (!Number.isInteger(numValue)) return "Floors allowed must be a whole number";
      //     return null;
      //   },
      // },
      boundaryWall: {
  required: true,
  custom: (value) => {
    if (value === null) return "Boundary wall is required";
    return null;
  },
},
    }),
    []
  );

  // ---------- HELPER FUNCTIONS ----------
  const normalize = useCallback(
    (stringValue) => String(stringValue || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const matchAttr = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);
      return Array.isArray(attributeValues)
        ? attributeValues.filter(
            (item) => item?.isActive && normalize(item.attrName) === matchAttr
          )
        : [];
    },
    [FIELD_TO_ATTRIBUTE_MAP, attributeValues, normalize]
  );

  // ---------- FIELD UPDATE HANDLER ----------
  const updateField = useCallback((fieldKey, value) => {
    setForm((prev) => ({ ...prev, [fieldKey]: value }));
    setTouched((prev) => ({ ...prev, [fieldKey]: true }));
  }, []);

  // ---------- VALIDATION ----------
  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;
      
      const value = form[field];

      if (rule.required) {
        if (value === "" || value === null || value === undefined) {
          return rule.message || `${field} is required`;
        }
    
      }

      if (rule.custom) {
        return rule.custom(value);
      }

      return null;
    },
    [validationRules, form]
  );

  const hasErrors = useCallback(
    () => {
      const requiredFields = ["plotArea", "plotAreaUnit", "floorsAllowed"];
      return requiredFields.some((field) => !!getFieldError(field));
    },
    [getFieldError]
  );

  const handleBlur = useCallback(
    (field) => setTouched((prev) => ({ ...prev, [field]: true })),
    []
  );

  // ---------- INITIALIZE FORM ----------
  useEffect(() => {
    if (!data || !attributeValues.length || isInitialized) return;

    const propertyData = Array.isArray(data) ? data[0] : data;
    const attributes = propertyData?.attributes || {};
    const areaInfo = propertyData?.area_information || {};

    const updatedForm = {
      plotArea: areaInfo.land_plot_area || areaInfo.plot_area || attributes.plot_area || "",
      length: areaInfo.length || attributes.length || "",
      width: areaInfo.width || attributes.width || "",
      floorsAllowed: attributes.floors_allowed_for_construction || "",
      boundaryWall: Boolean(attributes.boundary_wall),
      gatedProject: Boolean(attributes.gated_project),
      plotAreaUnit: areaInfo.plot_area_unit || attributes.plot_area_unit || "",
      facing: attributes.facing || "",
      projectName: attributes.project_name || "", 
    };

    setForm(updatedForm);
    setIsInitialized(true);
  }, [data, attributeValues, isInitialized]);



// ---------- CALCULATE WIDTH FROM PLOT AREA (UNIT-AWARE) ----------
useEffect(() => {
  if (form.plotArea === "" || form.length === "") {
    setForm((prev) => (prev.width === "" ? prev : { ...prev, width: "" }));
    return;
  }

  const plotArea = parseFloat(form.plotArea);
  const length = parseFloat(form.length);

  if (!Number.isFinite(plotArea) || !Number.isFinite(length) || plotArea <= 0 || length <= 0) {
    setForm((prev) => (prev.width === "" ? prev : { ...prev, width: "" }));
    return;
  }

  // --- NEW: resolve the stored unit id (e.g. "15") to its label (e.g. "Cent") ---
  const unitOptions = getOptions("plotAreaUnit") || [];
  const matchedUnit = unitOptions.find(
    (item) =>
      String(item.id) === String(form.plotAreaUnit) ||
      String(item.label || item.attr_val_name || "").toLowerCase() ===
        String(form.plotAreaUnit || "").toLowerCase()
  );
  const unitLabel = matchedUnit?.label ?? matchedUnit?.attr_val_name ?? form.plotAreaUnit;

  const normalizeUnit = (u) =>
    String(u || "").trim().toLowerCase().replace(/[\s_.]/g, "");

  const factor = UNIT_TO_SQFT[normalizeUnit(unitLabel)] ?? 1;

  const physicalAreaSqFt = plotArea * factor;
  const calculated = Number((physicalAreaSqFt / length).toFixed(2)).toString();

  setForm((prev) => (prev.width === calculated ? prev : { ...prev, width: calculated }));
}, [form.plotArea, form.length, form.plotAreaUnit, UNIT_TO_SQFT, getOptions]);

  // ---------- COMMON FIELDS ----------
  const { renderNumberField, renderDropdown, renderTextInput, } = CommonFields({
    form,
    setForm,
    touched,
    handleBlur,
    getFieldError,
    getOptions,
    attributeValues,
    updateField,
  });

  const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  // ---------- SAVE HANDLER ----------
  const runValidation = useCallback(() => {
    const requiredFields = ["plotArea", "plotAreaUnit", "floorsAllowed"];
    const allTouched = requiredFields.reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );
    setTouched((prev) => ({ ...prev, ...allTouched }));
    

    if (hasErrors()) {
      setError("Please fill all required fields correctly");
      return false;
    }
    return true;
  }, [hasErrors]);

  const handleSaveInternal = useCallback(
    async (navigateNext = false) => {
      const isValid = runValidation();
      if (!isValid) return;

      setSaving(true);
      setError("");
      setSuccess("");

      try {
        const attributes = {
          length: form.length ,
          width: form.width ,
          floors_allowed_for_construction: form.floorsAllowed,
          plot_area_unit: form.plotAreaUnit,
          boundary_wall: form.boundaryWall,
          gated_project: form.gatedProject,
          facing: form.facing,
          project_name: form.projectName || "",
        };

        if (onSaveSuccess) {
          await onSaveSuccess(form, attributes);
        }

    
       if (navigateNext && onNavigateNext) {
          onNavigateNext(form);
        }
      } catch (err) {
        console.error("Save failed:", err);
        setError(err.message || "Failed to save land/plot details");
      } finally {
        setSaving(false);
      }
    },
    [form, onSaveSuccess, onNavigateNext, runValidation]
  );

  const handleNext = useCallback(
    () => handleSaveInternal(true),
    [handleSaveInternal]
  );

  const handleSaveOnly = useCallback(
    () => handleSaveInternal(false),
    [handleSaveInternal]
  );

  // Handle gated project toggle
  const handleGatedProjectToggle = useCallback((toggled) => {
    updateField("gatedProject", toggled);
    // Optional: Clear project name if toggled off (optional)
    if (!toggled) {
      updateField("projectName", "");
    }
  }, [updateField]);

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          lowContrast
          subtitle={error}
          timeout={5000}
          onClose={() => setError("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}
      
      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", background: "#fff" }}>
              <Stack gap={6}>
                {/* Plot Area with Unit */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                        <div style={{ flex: 2 }}>
                          {renderNumberField("plotArea", "Plot Area", "0", true)}
                        </div>
                        <div style={{ flex: 1 }}>
                          {renderDropdown("plotAreaUnit", "Unit", true)}
                        </div>
                      </div>
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Length */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderNumberField("length", "Length", "0", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Width */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderNumberField("width", "Width", "0", false)}
                      
{form.plotArea && form.length && form.width && (
  <div style={{ marginTop: "0.5rem" }}>
    <p
      style={{
        fontSize: "0.875rem",
        color: "#161616",
        margin: 0,
        fontWeight: "500",
      }}
    >
      Calculated width: {form.width} ft
    </p>
  </div>
)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Floors Allowed - Required Field */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <span
                          style={{ 
                            fontSize: "0.875rem", 
                            fontWeight: "600",
                            color: "#161616"
                          }}
                        >
                          Floors allowed for construction
                        </span>
                        <Tooltip
                          align="top"
                          label="Maximum number of floors allowed for construction as prescribed by the local regulatory body"
                        >
                          <Button
                            kind="ghost"
                            hasIconOnly
                            renderIcon={Information}
                            iconDescription="Information"
                            size="sm"
                            style={{ padding: 0, minHeight: "auto" }}
                          />
                        </Tooltip>
                      </div>
                      
                      {renderNumberField(
                        "floorsAllowed",
                        "Floors",
                        "Enter number of floors",
false                      )}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Toggle Fields with Yes/No Labels */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      <Toggle
                        id="boundaryWall"
                        labelText={requiredLabel("Boundary Wall")}
                        labelA="No"
                        labelB="Yes"
                        toggled={form.boundaryWall}
                        onToggle={(toggled) => {updateField("boundaryWall", toggled);}}
                        style={{ marginBottom: "1rem" }}
                        
                       
                      />
                    </Column>
                  </Grid>
                </FormGroup>

                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      <Toggle
                        id="gatedProject"
                        labelText="Inside Gated Project"
                        labelA="No"
                        labelB="Yes"
                        toggled={form.gatedProject}
                        onToggle={handleGatedProjectToggle}
                      />
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Conditional Project Name Field (Optional) */}
                {form.gatedProject && (
                  <FormGroup>
                    <Grid>
                      <Column sm={4} md={8} lg={8}>
                        {renderTextInput("projectName", "Project Name", "Enter project name", false)}
                      </Column>
                    </Grid>
                  </FormGroup>
                )}

                <FormGroup>
                  <Grid>
                    <Column sm={4} md={8} lg={8}>
                      {renderDropdown("facing", "Facing",  false

                      )}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Action Buttons */}
                {CommonButtons && (
                  <CommonButtons
                    onBack={onBack}
                    onSave={handleSaveOnly}
                    onNext={handleNext}
                    showBack={true}
                    showSave={true}
                    showNext={true}
                    isSaveLoading={saving}
                    isNextLoading={saving}
                    stepData={form}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default PropertyDetails;